import clsx from 'clsx';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarDaysIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { ClientVacationPeriod } from '../../../../../models/vacationClientView.types';
import { VacationAvatar } from '../../../../VacationDpTable/components/VacationAvatar';
import { getPanelStatusLabel, getStatusBadgeClass, formatPeriodRange, getSegmentTitle } from '../../../vacationClientPanel.definitions';
import { CollaboratorNameLink } from '../../CollaboratorNameLink';

interface PeriodHeaderProps {
    period: ClientVacationPeriod;
    clientName?: string;
    responsibleName: string;
}

interface InfoBoxProps {
    icon: React.ComponentType<{ className: string }>;
    title: string;
    bgColor: string;
    borderColor: string;
    iconBg: string;
    children?: React.ReactNode;
    value?: string;
    subtitle?: string;
}

function InfoBox({ icon: Icon, title, bgColor, borderColor, iconBg, children, value, subtitle }: InfoBoxProps) {
    return (
        <div className={clsx('rounded-lg border p-3', borderColor, bgColor)}>
            <p className={clsx('flex items-center gap-1.5 text-xs font-semibold mb-2', borderColor.replace('border-', 'text-').replace('-100', '-800'))}>
                <span className={clsx('rounded p-1', iconBg)}>
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                </span>
                {title}
            </p>
            {children ? (
                <div className="space-y-1 text-sm text-raro-gray-900">{children}</div>
            ) : (
                <>
                    <p className="text-sm font-medium text-raro-gray-900">{value}</p>
                    {subtitle && <p className="text-xs text-raro-gray-500 mt-1">{subtitle}</p>}
                </>
            )}
        </div>
    );
}

export function PeriodHeader({ period, clientName, responsibleName }: PeriodHeaderProps) {
    return (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.8fr)]">
            <div className="flex items-start gap-3">
                <VacationAvatar name={period.collaboratorName} photoUrl={period.collaboratorPhotoUrl} size="lg" />
                <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold text-raro-gray-900">
                        <CollaboratorNameLink collaboratorId={period.collaboratorId} name={period.collaboratorName} />
                    </h3>
                    <span
                        className={clsx(
                            'mt-1.5 inline-flex items-center rounded px-2 py-1 text-xs font-medium',
                            getStatusBadgeClass(period.status)
                        )}
                    >
                        {getPanelStatusLabel(period.status)}
                    </span>
                    <p className="mt-2 text-sm text-raro-gray-600">
                        {clientName && `${clientName} · `}Responsável: {responsibleName} · {period.totalDays} dias
                    </p>
                </div>
            </div>

            <InfoBox
                icon={CalendarDaysIcon}
                title="Períodos solicitados"
                bgColor="bg-primary-50/60"
                borderColor="border-primary-100"
                iconBg="bg-primary-100 text-primary-600"
            >
                {period.segments.map((segment, idx) => (
                    <div key={idx}>
                        {period.segments.length > 1 ? `${getSegmentTitle(idx + 1, period.segments.length)} · ` : ''}
                        {formatPeriodRange(segment.startDate, segment.endDate)} ({segment.days}d)
                    </div>
                ))}
            </InfoBox>

            <InfoBox
                icon={ClockIcon}
                title="Data da solicitação"
                bgColor="bg-sky-50/60"
                borderColor="border-sky-100"
                iconBg="bg-sky-100 text-sky-600"
                value={period.requestedAt ? format(period.requestedAt, 'dd/MM/yyyy', { locale: ptBR }) : '—'}
                subtitle={`por ${period.collaboratorName}`}
            />
        </div>
    );
}
